'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { colors } from '@/constant/colors';
import { logReactError } from '@/lib/errorLogger';
import NormalButton from '@/components/Button/normal';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

/**
 * ErrorBoundary 组件
 *
 * 用于捕获 React 组件树中的 JavaScript 错误，记录错误并显示友好的错误提示
 *
 * 特性：
 * - 捕获子组件树的渲染错误
 * - 显示友好的错误提示界面（"我们遇到了一些问题"）
 * - 提供"Go Home"按钮返回首页
 * - 自动记录错误到控制台
 * - 自动上报错误到 PostHog
 * - 支持自定义 fallback UI
 *
 * 使用方式：
 * ```tsx
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // 更新 state 使下一次渲染能够显示降级 UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // 记录错误信息
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // 更新状态
    this.setState({
      error,
      errorInfo,
    });

    // 使用错误日志服务记录错误
    logReactError(error, errorInfo, 'error_boundary');

    // 调用自定义错误处理函数
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentWillUnmount() {
    // 清理定时器
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  handleRetry = () => {
    const { retryCount } = this.state;

    if (retryCount >= this.maxRetries) {
      console.warn('Max retry attempts reached');
      return;
    }

    console.log(`Retrying... (attempt ${retryCount + 1}/${this.maxRetries})`);

    // 重置错误状态
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: retryCount + 1,
    });
  };

  handleReload = () => {
    // 刷新页面
    window.location.reload();
  };

  handleGoHome = () => {
    // 返回首页
    window.location.href = '/quest';
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // 如果提供了自定义 fallback，使用它（主要用于测试）
      if (fallback) {
        return fallback;
      }

      // 显示友好的错误提示UI
      return (
        <div className="w-full h-screen flex flex-col justify-center items-center gap-8 px-4">
          <div className="text-center">
            <h1
              className="emphasize-text text-3xl mb-4"
              style={{ color: colors.mainTextColor }}
            >
              Something went wrong
            </h1>
            <p
              className="display-text text-lg"
              style={{ color: colors.mainTextColor }}
            >
              Don't worry, let's go back to the home page
            </p>
          </div>

          <NormalButton
            onClick={this.handleGoHome}
            colorType="green"
            className="w-48"
          >
            Go Home
          </NormalButton>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
